import {useCallback, useMemo} from 'react';
import {v4 as uuidv4} from 'uuid';
import {toast} from 'react-toastify';
import {
    ResourceAllocationState,
    Project,
    ProjectPhase,
    ProjectPerson
} from '../../../types';
import {getNextPhaseColor} from '../../../lib/phaseUtils';
import {removeProjectCascade} from '../cascadeDelete';

// The backend rejects granting canEdit/canEditResources to a person without a
// maintainer seat with a 400 whose message starts "target_not_maintainer:".
// Surface that as a friendly toast instead of a silent optimistic revert.
const notifyIfTargetNotMaintainer = (errorText: string) => {
    if (errorText.includes('target_not_maintainer')) {
        toast.error(
            'That person needs a maintainer seat before they can be granted edit access. Grant them the Maintainer role (or Administrator) first.'
        );
    }
};

// The server rejects a project code that is too long or already used by
// another project in the company. Pull its friendly message out of the
// problem details body so the user sees why the save didn't stick.
const projectCodeError = (errorText: string): string | null => {
    try {
        const body = JSON.parse(errorText) as {
            errors?: {message?: string}[];
        };
        const message = body.errors?.[0]?.message ?? null;
        return message && /\bcode\b/i.test(message) ? message : null;
    } catch {
        return null;
    }
};

type AuthenticatedFetch = (
    url: string,
    options?: RequestInit
) => Promise<Response>;

interface SignalRConnection {
    getConnectionId(): string | null;
}

interface ActionDeps {
    // Ref, not the state value: callbacks read state via stateRef.current so
    // their identities survive state changes — the provider value can then be
    // memoized and downstream memo boundaries hold.
    stateRef: {current: ResourceAllocationState};
    setState: React.Dispatch<React.SetStateAction<ResourceAllocationState>>;
    authenticatedFetch: AuthenticatedFetch;
    signalRService: SignalRConnection;
    customerId: string | undefined;
    baseUrl: string;
}

export const useProjectActions = ({
    stateRef,
    setState,
    authenticatedFetch,
    signalRService,
    customerId,
    baseUrl
}: ActionDeps) => {
    // Project operations
    const addProject = useCallback(
        async (project: Omit<Project, 'id'>): Promise<string> => {
            const id = uuidv4();
            const newProject = {...project, id};

            // Optimistic update
            setState(prev => ({
                ...prev,
                projects: {...prev.projects, [id]: newProject}
            }));

            // API call — awaited so callers can sequence dependent creates and
            // surface failures (the server persists this same client-generated id).
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                const revert = () =>
                    setState(prev => {
                        const {[id]: _removed, ...rest} = prev.projects;
                        return {...prev, projects: rest};
                    });

                try {
                    const response = await authenticatedFetch(
                        `${baseUrl}/api/resource-allocation/projects`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-SignalR-Connection-Id': connectionId || ''
                            },
                            body: JSON.stringify({
                                id: id,
                                name: project.name,
                                description: project.description,
                                code: project.code ?? null,
                                startGlobalWeekNumber:
                                    project.startGlobalWeekNumber,
                                startOn: project.startOn,
                                endGlobalWeekNumber:
                                    project.endGlobalWeekNumber,
                                endOn: project.endOn,
                                status: project.status,
                                visibility: project.visibility,
                                certainty: project.certainty,
                                confidencePercent:
                                    project.confidencePercent ?? null
                            })
                        }
                    );

                    if (!response.ok) {
                        const errorText = await response.text();
                        console.error(
                            '[addProject] API call failed:',
                            errorText
                        );
                        revert();
                        throw new Error(
                            projectCodeError(errorText) ??
                                `Failed to create project: ${errorText}`
                        );
                    }
                } catch (error: unknown) {
                    console.error('[addProject] API call error:', error);
                    revert();
                    throw error;
                }
            }

            return id;
        },
        [
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const updateProject = useCallback(
        (id: string, updates: Partial<Project>) => {
            const state = stateRef.current;
            const previousProject = state.projects[id];

            // Optimistic update
            setState(prev => ({
                ...prev,
                projects: {
                    ...prev.projects,
                    [id]: {...prev.projects[id], ...updates}
                }
            }));

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/projects/${id}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify(updates)
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            const errorText = await response.text();
                            console.error(
                                '[updateProject] API call failed:',
                                errorText
                            );
                            const codeError = projectCodeError(errorText);
                            if (codeError) {
                                toast.error(codeError);
                            }
                            setState(prev => ({
                                ...prev,
                                projects: {
                                    ...prev.projects,
                                    [id]: previousProject
                                }
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[updateProject] API call error:', error);
                        setState(prev => ({
                            ...prev,
                            projects: {...prev.projects, [id]: previousProject}
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Atomic whole-project time shift (server-side: project + phases + EVERY
    // allocation week, one transaction). Deliberately NO optimistic update:
    // the server broadcasts ProjectShifted to everyone including this client,
    // so there is exactly one convergence path. Awaited so the resume dialog
    // can sequence "shift, then activate" and surface failures.
    const shiftProject = useCallback(
        async (
            id: string,
            offsetWeeks: number,
            fromWeek?: number | null
        ): Promise<boolean> => {
            if (!customerId || !authenticatedFetch || offsetWeeks === 0) {
                return offsetWeeks === 0;
            }
            try {
                const response = await authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/projects/${id}/shift`,
                    {
                        method: 'POST',
                        headers: {'Content-Type': 'application/json'},
                        body: JSON.stringify({
                            offsetWeeks,
                            fromWeek: fromWeek ?? null
                        })
                    }
                );
                if (!response.ok) {
                    const text = await response.text();
                    console.error('[shiftProject] API call failed:', text);
                    toast.error(
                        'Could not move the project. Nothing was changed.'
                    );
                    return false;
                }
                return true;
            } catch (error) {
                console.error('[shiftProject] API call error:', error);
                toast.error('Could not move the project. Nothing was changed.');
                return false;
            }
        },
        [authenticatedFetch, customerId, baseUrl]
    );

    const deleteProject = useCallback(
        (id: string) => {
            const state = stateRef.current;
            // Snapshot every collection the cascade touches, for revert on failure.
            const removedAssignmentIds = new Set(
                Object.values(state.assignments)
                    .filter(a => a.projectId === id)
                    .map(a => a.id)
            );
            const backup = {
                project: state.projects[id],
                phases: Object.fromEntries(
                    Object.entries(state.phases).filter(
                        ([, phase]) => phase.projectId === id
                    )
                ),
                assignments: Object.fromEntries(
                    Object.entries(state.assignments).filter(
                        ([, a]) => a.projectId === id
                    )
                ),
                allocations: Object.fromEntries(
                    Object.entries(state.allocations).filter(([, al]) =>
                        removedAssignmentIds.has(al.assignmentId)
                    )
                ),
                projectTags: Object.fromEntries(
                    Object.entries(state.projectTags).filter(
                        ([, pt]) => pt.projectId === id
                    )
                ),
                projectPeople: Object.fromEntries(
                    Object.entries(state.projectPeople).filter(
                        ([, pp]) => pp.projectId === id
                    )
                )
            };

            // Optimistic update — full cascade (mirrors the DB ON DELETE CASCADE).
            setState(prev => removeProjectCascade(prev, id));

            const revert = () =>
                setState(prev => ({
                    ...prev,
                    projects: {...prev.projects, [id]: backup.project},
                    phases: {...prev.phases, ...backup.phases},
                    assignments: {...prev.assignments, ...backup.assignments},
                    allocations: {...prev.allocations, ...backup.allocations},
                    projectTags: {...prev.projectTags, ...backup.projectTags},
                    projectPeople: {
                        ...prev.projectPeople,
                        ...backup.projectPeople
                    }
                }));

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/projects/${id}`,
                    {
                        method: 'DELETE',
                        headers: {
                            'X-SignalR-Connection-Id': connectionId || ''
                        }
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[deleteProject] API call failed:',
                                await response.text()
                            );
                            revert();
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[deleteProject] API call error:', error);
                        revert();
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    // Phase operations
    const addPhase = useCallback(
        async (
            phase: Omit<ProjectPhase, 'id' | 'createdAt' | 'updatedAt'>
        ): Promise<string> => {
            const state = stateRef.current;
            const id = uuidv4();
            const now = new Date().toISOString();

            // Calculate based on current state
            const projectPhases = Object.values(state.phases)
                .filter(p => p.projectId === phase.projectId)
                .sort(
                    (a, b) => a.startGlobalWeekNumber - b.startGlobalWeekNumber
                );

            const colour = phase.colour || getNextPhaseColor(projectPhases);

            const newPhase = {
                ...phase,
                id,
                colour,
                createdAt: now,
                updatedAt: now
            };

            setState(prev => ({
                ...prev,
                phases: {
                    ...prev.phases,
                    [id]: newPhase
                }
            }));

            // API call — awaited so callers can sequence dependent creates and
            // surface failures (the server persists this same client-generated id).
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();
                const phaseToSend = newPhase;

                const revert = () =>
                    setState(prev => {
                        const {[id]: _removed, ...rest} = prev.phases;
                        return {...prev, phases: rest};
                    });

                try {
                    const response = await authenticatedFetch(
                        `${baseUrl}/api/resource-allocation/project-phases`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-SignalR-Connection-Id': connectionId || ''
                            },
                            body: JSON.stringify({
                                id: phaseToSend.id,
                                projectId: phaseToSend.projectId,
                                name: phaseToSend.name,
                                description: phaseToSend.description,
                                startGlobalWeekNumber:
                                    phaseToSend.startGlobalWeekNumber,
                                endGlobalWeekNumber:
                                    phaseToSend.endGlobalWeekNumber,
                                durationInWeeks: phaseToSend.durationInWeeks,
                                startOn: phaseToSend.startOn,
                                endOn: phaseToSend.endOn,
                                type: phaseToSend.type,
                                colour: phaseToSend.colour
                            })
                        }
                    );

                    if (!response.ok) {
                        const errorText = await response.text();
                        console.error('[addPhase] API call failed:', errorText);
                        revert();
                        throw new Error(`Failed to create phase: ${errorText}`);
                    }
                } catch (error: unknown) {
                    console.error('[addPhase] API call error:', error);
                    revert();
                    throw error;
                }
            }

            return id;
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const updatePhase = useCallback(
        (id: string, updates: Partial<ProjectPhase>) => {
            const previousPhase = stateRef.current.phases[id];

            // Optimistic update
            setState(prev => ({
                ...prev,
                phases: {
                    ...prev.phases,
                    [id]: {
                        ...prev.phases[id],
                        ...updates,
                        updatedAt: new Date().toISOString()
                    }
                }
            }));

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/project-phases/${id}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify(updates)
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[updatePhase] API call failed:',
                                await response.text()
                            );
                            // Revert on failure
                            setState(prev => ({
                                ...prev,
                                phases: {...prev.phases, [id]: previousPhase}
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[updatePhase] API call error:', error);
                        // Revert on error
                        setState(prev => ({
                            ...prev,
                            phases: {...prev.phases, [id]: previousPhase}
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const deletePhase = useCallback(
        (id: string) => {
            const previousPhase = stateRef.current.phases[id];

            // Optimistic update
            setState(prev => {
                const {[id]: _removed, ...rest} = prev.phases;
                return {...prev, phases: rest};
            });

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/project-phases/${id}`,
                    {
                        method: 'DELETE',
                        headers: {
                            'X-SignalR-Connection-Id': connectionId || ''
                        }
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[deletePhase] API call failed:',
                                await response.text()
                            );
                            // Revert on failure
                            setState(prev => ({
                                ...prev,
                                phases: {...prev.phases, [id]: previousPhase}
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('[deletePhase] API call error:', error);
                        // Revert on error
                        setState(prev => ({
                            ...prev,
                            phases: {...prev.phases, [id]: previousPhase}
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const getProjectPhases = useCallback(
        (projectId: string): ProjectPhase[] => {
            return Object.values(stateRef.current.phases)
                .filter(phase => phase.projectId === projectId)
                .sort(
                    (a, b) => a.startGlobalWeekNumber - b.startGlobalWeekNumber
                );
        },
        [stateRef]
    );

    // Project-Person CRUD operations
    const addProjectPerson = useCallback(
        (projectPerson: Omit<ProjectPerson, 'id' | 'createdAt'>): string => {
            const id = uuidv4();
            const now = new Date().toISOString();
            const newProjectPerson = {...projectPerson, id, createdAt: now};

            // Optimistic update
            setState(prev => ({
                ...prev,
                projectPeople: {...prev.projectPeople, [id]: newProjectPerson}
            }));

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/project-people`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify({
                            id,
                            projectId: projectPerson.projectId,
                            personId: projectPerson.personId,
                            projectRole: projectPerson.projectRole,
                            canEdit: projectPerson.canEdit,
                            canEditResources: projectPerson.canEditResources
                        })
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            const errorText = await response.text();
                            console.error(
                                '[addProjectPerson] API call failed:',
                                errorText
                            );
                            notifyIfTargetNotMaintainer(errorText);
                            // Revert on failure
                            setState(prev => {
                                const {[id]: _removed, ...rest} =
                                    prev.projectPeople;
                                return {...prev, projectPeople: rest};
                            });
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[addProjectPerson] API call error:',
                            error
                        );
                        setState(prev => {
                            const {[id]: _removed, ...rest} = prev.projectPeople;
                            return {...prev, projectPeople: rest};
                        });
                    });
            }

            return id;
        },
        [
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const updateProjectPerson = useCallback(
        (id: string, updates: Partial<ProjectPerson>) => {
            const previousProjectPerson = stateRef.current.projectPeople[id];

            // Optimistic update
            setState(prev => ({
                ...prev,
                projectPeople: {
                    ...prev.projectPeople,
                    [id]: {...prev.projectPeople[id], ...updates}
                }
            }));

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/project-people/${id}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SignalR-Connection-Id': connectionId || ''
                        },
                        body: JSON.stringify(updates)
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            const errorText = await response.text();
                            console.error(
                                '[updateProjectPerson] API call failed:',
                                errorText
                            );
                            notifyIfTargetNotMaintainer(errorText);
                            // Revert
                            setState(prev => ({
                                ...prev,
                                projectPeople: {
                                    ...prev.projectPeople,
                                    [id]: previousProjectPerson
                                }
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[updateProjectPerson] API call error:',
                            error
                        );
                        setState(prev => ({
                            ...prev,
                            projectPeople: {
                                ...prev.projectPeople,
                                [id]: previousProjectPerson
                            }
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const removeProjectPerson = useCallback(
        (id: string) => {
            const previousProjectPerson = stateRef.current.projectPeople[id];

            // Optimistic update
            setState(prev => {
                const {[id]: _removed, ...rest} = prev.projectPeople;
                return {...prev, projectPeople: rest};
            });

            // API call
            if (customerId && authenticatedFetch) {
                const connectionId = signalRService.getConnectionId();

                authenticatedFetch(
                    `${baseUrl}/api/resource-allocation/project-people/${id}`,
                    {
                        method: 'DELETE',
                        headers: {
                            'X-SignalR-Connection-Id': connectionId || ''
                        }
                    }
                )
                    .then(async (response: Response) => {
                        if (!response.ok) {
                            console.error(
                                '[removeProjectPerson] API call failed:',
                                await response.text()
                            );
                            // Revert
                            setState(prev => ({
                                ...prev,
                                projectPeople: {
                                    ...prev.projectPeople,
                                    [id]: previousProjectPerson
                                }
                            }));
                        }
                    })
                    .catch((error: unknown) => {
                        console.error(
                            '[removeProjectPerson] API call error:',
                            error
                        );
                        setState(prev => ({
                            ...prev,
                            projectPeople: {
                                ...prev.projectPeople,
                                [id]: previousProjectPerson
                            }
                        }));
                    });
            }
        },
        [
            stateRef,
            setState,
            authenticatedFetch,
            signalRService,
            customerId,
            baseUrl
        ]
    );

    const getProjectPeople = useCallback(
        (projectId: string): ProjectPerson[] => {
            return Object.values(stateRef.current.projectPeople)
                .filter(pp => pp.projectId === projectId)
                .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
        },
        [stateRef]
    );

    const getPersonProjects = useCallback(
        (personId: string): string[] => {
            return Object.values(stateRef.current.projectPeople)
                .filter(pp => pp.personId === personId)
                .map(pp => pp.projectId);
        },
        [stateRef]
    );

    const setCurrentPersonId = useCallback(
        (personId: string | null) => {
            setState(prev => ({
                ...prev,
                currentPersonId: personId
            }));
        },
        [setState]
    );

    return useMemo(
        () => ({
            addProject,
            updateProject,
            shiftProject,
            deleteProject,
            addPhase,
            updatePhase,
            deletePhase,
            getProjectPhases,
            addProjectPerson,
            updateProjectPerson,
            removeProjectPerson,
            getProjectPeople,
            getPersonProjects,
            setCurrentPersonId
        }),
        [
            addProject,
            updateProject,
            shiftProject,
            deleteProject,
            addPhase,
            updatePhase,
            deletePhase,
            getProjectPhases,
            addProjectPerson,
            updateProjectPerson,
            removeProjectPerson,
            getProjectPeople,
            getPersonProjects,
            setCurrentPersonId
        ]
    );
};
